import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Plus, 
  RefreshCw, 
  Play, 
  Pause,
  Edit,
  Trash2,
  Key,
  Check,
  X,
  FileText,
  Eye
} from 'lucide-react';
import { Button, Card, Badge, Loading } from '../../components/common';
import tenantsService from '../../services/api/tenants.service';
import TenantModal from '../../components/dashboard/TenantModal/TenantModal';
import FlowUploadModal from '../../components/dashboard/FlowUploadModal/FlowUploadModal';
import TenantDetailsModal from '../../components/dashboard/TenantDetailsModal/TenantDetailsModal';

const Tenants = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [flowModalOpen, setFlowModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const data = await tenantsService.getAll();
      setTenants(data);
    } catch (error) {
      console.error('Erro ao carregar tenants:', error);
      alert('Erro ao carregar tenants: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedTenant(null);
    setModalOpen(true);
  };

  const handleEdit = async (tenant, e) => {
    if (e) e.stopPropagation();
    
    try {
      // Buscar dados completos do tenant
      const fullTenant = await tenantsService.getById(tenant.id);
      setSelectedTenant(fullTenant);
      setModalOpen(true);
    } catch (error) {
      console.error('Erro ao carregar tenant completo:', error);
      // Fallback: usar o tenant da lista mesmo
      setSelectedTenant(tenant);
      setModalOpen(true);
    }
  };

  const handleViewDetails = (tenant, e) => {
    if (e) e.stopPropagation();
    setSelectedTenant(tenant);
    setDetailsModalOpen(true);
  };

  const handleDelete = async (id, e) => {
    if (e) e.stopPropagation();
    
    if (!window.confirm('Tem certeza que deseja deletar este tenant? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      await tenantsService.delete(id);
      alert('Tenant deletado com sucesso!');
      loadTenants();
    } catch (error) {
      alert('Erro ao deletar tenant: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const handleToggleBot = async (tenant, e) => {
    if (e) e.stopPropagation();
    
    try {
      if (tenant.botPaused) {
        await tenantsService.resumeBot(tenant.id);
        alert('Bot retomado com sucesso!');
      } else {
        await tenantsService.pauseBot(tenant.id);
        alert('Bot pausado com sucesso!');
      }
      loadTenants();
    } catch (error) {
      alert('Erro ao alterar status do bot: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const handleGenerateToken = async (id, e) => {
    if (e) e.stopPropagation();
    
    try {
      const result = await tenantsService.generateToken(id);
      
      // Criar modal customizado para copiar o token
      const tokenText = result.token;
      
      // Copiar automaticamente
      navigator.clipboard.writeText(tokenText);
      
      alert(`Token gerado e copiado para a área de transferência!\n\n${tokenText}\n\n⚠️ Guarde este token em local seguro. Ele não será exibido novamente.`);
    } catch (error) {
      alert('Erro ao gerar token: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const handleOpenFlowModal = (tenant, e) => {
    if (e) e.stopPropagation();
    setSelectedTenant(tenant);
    setFlowModalOpen(true);
  };

  const handleFlowUpload = async (flowId, yamlContent) => {
    try {
      await tenantsService.uploadFlow(selectedTenant.id, flowId, yamlContent);
      alert('Fluxo enviado com sucesso!');
      setFlowModalOpen(false);
      setSelectedTenant(null);
    } catch (error) {
      throw new Error(error.message || 'Erro ao fazer upload do fluxo');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Loading size="lg" text="Carregando tenants..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-primary-500" />
            Gerenciar Tenants
          </h1>
          <p className="text-gray-600 mt-1">
            Configure e gerencie clientes da plataforma
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="md"
            onClick={loadTenants}
            leftIcon={<RefreshCw className="w-4 h-4" />}
          >
            Atualizar
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleCreate}
            leftIcon={<Plus className="w-4 h-4" />}
          >
            Novo Tenant
          </Button>
        </div>
      </div>

      {/* Tenants Grid */}
      {tenants.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-12">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Nenhum tenant cadastrado
            </h3>
            <p className="text-gray-600 mb-4">
              Comece criando seu primeiro tenant
            </p>
            <Button
              variant="primary"
              onClick={handleCreate}
              leftIcon={<Plus className="w-4 h-4" />}
            >
              Criar Primeiro Tenant
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {tenants.map((tenant) => (
            <Card 
              key={tenant.id} 
              hoverable
              onClick={() => handleViewDetails(tenant)}
              className="cursor-pointer transition-all hover:shadow-lg"
            >
              <Card.Body>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">
                      {tenant.name}
                    </h3>
                    <p className="text-sm text-gray-500 truncate">ID: {tenant.id}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant={tenant.provider === 'official' || tenant.waProvider === 'official' ? 'primary' : 'secondary'}>
                        {tenant.provider === 'official' || tenant.waProvider === 'official' ? 'WhatsApp Official' : 'Evolution API'}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <Badge 
                      variant={tenant.isActive ? 'success' : 'danger'}
                      dot
                    >
                      {tenant.isActive ? 'Ativo' : 'Inativo'}
                    </Badge>
                    
                    <Badge 
                      variant={tenant.botPaused ? 'warning' : 'primary'}
                    >
                      {tenant.botPaused ? 'Bot Pausado' : 'Bot Ativo'}
                    </Badge>
                  </div>
                </div>

                {/* Info */}
                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex items-center gap-2">
                    {tenant.sendDisabled ? (
                      <X className="w-4 h-4 text-red-500" />
                    ) : (
                      <Check className="w-4 h-4 text-green-500" />
                    )}
                    <span className="text-gray-600">
                      Envio: {tenant.sendDisabled ? 'Desabilitado' : 'Habilitado'}
                    </span>
                  </div>

                  {tenant.coexEnable && (
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span className="text-gray-600">
                        Coexistência habilitada
                      </span>
                    </div>
                  )}
                  
                  {(tenant.provider === 'evolution' || tenant.waProvider === 'evolution') && tenant.evolutionInstance && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">
                        Instância: {tenant.evolutionInstance}
                      </span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div 
                  className="flex flex-wrap gap-2 pt-4 border-t border-gray-100"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => handleViewDetails(tenant, e)}
                    leftIcon={<Eye className="w-4 h-4" />}
                  >
                    Detalhes
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => handleToggleBot(tenant, e)}
                    leftIcon={tenant.botPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                  >
                    {tenant.botPaused ? 'Retomar' : 'Pausar'}
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => handleEdit(tenant, e)}
                    leftIcon={<Edit className="w-4 h-4" />}
                  >
                    Editar
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => handleOpenFlowModal(tenant, e)}
                    leftIcon={<FileText className="w-4 h-4" />}
                  >
                    Fluxo
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => handleGenerateToken(tenant.id, e)}
                    leftIcon={<Key className="w-4 h-4" />}
                  >
                    Token
                  </Button>

                  <Button
                    variant="danger"
                    size="sm"
                    onClick={(e) => handleDelete(tenant.id, e)}
                    leftIcon={<Trash2 className="w-4 h-4" />}
                  >
                    Deletar
                  </Button>
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}

      {/* Modals */}
      {modalOpen && (
        <TenantModal
          tenant={selectedTenant}
          onClose={() => {
            setModalOpen(false);
            setSelectedTenant(null);
          }}
          onSuccess={() => {
            setModalOpen(false);
            setSelectedTenant(null);
            loadTenants();
          }}
        />
      )}

      {flowModalOpen && selectedTenant && (
        <FlowUploadModal
          tenant={selectedTenant}
          onClose={() => {
            setFlowModalOpen(false);
            setSelectedTenant(null);
          }}
          onSuccess={handleFlowUpload}
        />
      )}

      {detailsModalOpen && selectedTenant && (
        <TenantDetailsModal
          tenantId={selectedTenant.id}
          onClose={() => {
            setDetailsModalOpen(false);
            setSelectedTenant(null);
          }}
          onEdit={(tenant) => {
            setDetailsModalOpen(false);
            setSelectedTenant(tenant);
            setModalOpen(true);
          }}
        />
      )}
    </div>
  );
};

export default Tenants;